import {
  AudienceType,
  MatchingCriteria,
  PartnerPermissionTypes,
  WorkflowType,
} from '@/client/services/api/graphql/gql/graphql';
import { Box, Button, Flex, HStack, Stack, Text } from '@chakra-ui/react';
import { Check, Close } from '@/client/components/icons/ContinuIcons';

import AssignmentBuilderFormContainer from '../layout/AssignmentBuilderFormContainer';
import type { AssignmentFormData } from '../../types/assignmentTypes';
import { format } from 'date-fns';
import { useAdminAssignments } from '../../hooks/useAdminAssignments';
import { useAdminAssignmentsStore } from '../../state/adminAssignmentsStore';
import { useFormContext } from 'react-hook-form';
import { useGetAssignentAudienceCount } from '../hooks/useGetAssignentAudienceCount';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface PublishSectionProps {
  title: string;
  onEditClick: () => void;
  children?: React.ReactNode;
  useBottomBorder?: boolean;
  renderEditButton?: boolean;
}

function PublishSection({
  title,
  onEditClick,
  children,
  useBottomBorder = true,
  renderEditButton = true,
}: PublishSectionProps) {
  const { t } = useTranslation();

  return (
    <Box
      paddingY={7}
      paddingX={6}
      borderBottom="1px solid"
      borderBottomColor={useBottomBorder ? 'neutral.100' : 'transparent'}
    >
      <Flex justifyContent="space-between" alignItems="center">
        <Stack>
          <HStack>
            <Check />

            <Text variant="createHeading">{title}</Text>
          </HStack>

          {children && <Box paddingLeft={7}>{children}</Box>}
        </Stack>

        {renderEditButton && (
          <Button
            border="2px solid"
            borderColor="warmNeutral.300"
            borderRadius="6px"
            paddingY={6}
            paddingX={5}
            fontSize="16px"
            fontWeight={600}
            color="baseBlack"
            backgroundColor="white"
            _hover={{
              backgroundColor: 'warmNeutral.0',
            }}
            onClick={onEditClick}
          >
            {t('admin.assignments.publish.edit')}
          </Button>
        )}
      </Flex>
    </Box>
  );
}

function PreviewCheck() {
  return <Check boxSize={3} />;
}

function PreviewX() {
  return <Close boxSize={3} />;
}

export default function AssignmentPublishForm() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { watch } = useFormContext<AssignmentFormData>();
  const { setStepIndex } = useAdminAssignmentsStore();
  const { automationTypeTranslationMap } = useAdminAssignments();

  const { settings, audience, customDueDate, useCustomDueDate, simpleType } = watch();

  const {
    type,
    name,
    responses,
    conditionals,
    nonReminderMessages,
    reminderMessages,
    overdueMessages,
    daysToComplete,
    requireRecompletion,
    escalateManager,
    escalateBuddy,
    escalationContacts,
  } = settings;

  const matchAnyAudience = audience.filter(
    (item) => item.matchingCriteria === MatchingCriteria.MatchAny,
  );
  const matchAllAudience = audience.filter(
    (item) =>
      item.matchingCriteria === MatchingCriteria.MatchAll &&
      item.type !== AudienceType.HiredDate &&
      item.type !== AudienceType.PartnerType,
  );
  const matchNoneAudience = audience.filter(
    (item) => item.matchingCriteria === MatchingCriteria.MatchNone,
  );

  const hasHiredDateFilter = audience.some((a) => a.type === AudienceType.HiredDate);

  const hasPartnerTypeFilter = audience.some((a) => a.type === AudienceType.PartnerType);

  const partnerPermissionItems = audience.filter((a) => a.type === AudienceType.PartnerType);

  const hasPartner = partnerPermissionItems.some(
    (item) => item.id === PartnerPermissionTypes.Partner,
  );
  const hasTenant = partnerPermissionItems.some(
    (item) => item.id === PartnerPermissionTypes.Tenant,
  );

  const getPartnerPermissionType = () => {
    if (hasPartner && hasTenant) {
      return PartnerPermissionTypes.PartnerAndTenant;
    }
    if (hasPartner) {
      return PartnerPermissionTypes.Partner;
    }
    if (hasTenant) {
      return PartnerPermissionTypes.Tenant;
    }
    return PartnerPermissionTypes.Partner; // Default to Partner instead of null
  };

  const partnerPermissionTypeMap = {
    [PartnerPermissionTypes.Tenant]: t(
      'admin.assignments.audience.extend.extendAccess.instanceOnly',
    ),
    [PartnerPermissionTypes.Partner]: t(
      'admin.assignments.audience.extend.extendAccess.allExtendAccounts',
    ),
    [PartnerPermissionTypes.PartnerAndTenant]: t(
      'admin.assignments.audience.extend.extendAccess.bothExtendAndInstance',
    ),
  };

  const { data: audienceCountData } = useGetAssignentAudienceCount(audience);

  const sectionOptions = [
    {
      title: t('admin.assignments.publish.details.label'),
      renderEditButton: !id,
      onEditClick: () => setStepIndex(0),
      children: (
        <Stack>
          <Text variant="createHelpText">{name}</Text>

          <Text variant="createHelpText">
            {type === WorkflowType.PointInTime
              ? t('admin.assignments.publish.details.directAssignment')
              : t('admin.assignments.publish.details.automatedAssignment')}
          </Text>

          {(type === WorkflowType.UserAddedOrUpdated || type === WorkflowType.UserUpdated) &&
            conditionals.length > 0 && (
              <Text variant="createHelpText">
                {`${automationTypeTranslationMap[type]} (${conditionals
                  .map((conditional) => conditional.name)
                  .join(', ')})`}
              </Text>
            )}
        </Stack>
      ),
    },
    {
      title: t('admin.assignments.publish.audience.label'),
      renderEditButton: simpleType === 'automated' || !id,
      onEditClick: () => setStepIndex(simpleType === 'pointInTime' ? 1 : 2),
      children: (
        <Stack>
          <Text variant="createHelpText">{`${audienceCountData ?? 0} users`}</Text>

          {matchAnyAudience.length > 0 && (
            <Text variant="createHelpText">
              {matchAnyAudience.length} {t('admin.assignments.publish.audience.matchAny')}
            </Text>
          )}

          {matchAllAudience.length > 0 && (
            <Text variant="createHelpText">
              {matchAllAudience.length} {t('admin.assignments.publish.audience.matchAll')}
            </Text>
          )}

          {matchNoneAudience.length > 0 && (
            <Text variant="createHelpText">
              {matchNoneAudience.length} {t('admin.assignments.publish.audience.matchNone')}
            </Text>
          )}

          {hasHiredDateFilter && (
            <Text variant="createHelpText">
              {t('admin.assignments.audience.additionalFilters.hiredWithinDays', {
                days: audience.find((a) => a.type === AudienceType.HiredDate)?.days || '#',
              })}
            </Text>
          )}

          {hasPartnerTypeFilter && (
            <Text variant="createHelpText">
              {t('admin.assignments.audience.extend.extendAccess')}{' '}
              {partnerPermissionTypeMap[getPartnerPermissionType()]}
            </Text>
          )}
        </Stack>
      ),
    },
    {
      title: t('admin.assignments.publish.content.label'),
      renderEditButton: simpleType === 'automated' || !id,
      onEditClick: () => setStepIndex(simpleType === 'pointInTime' ? 2 : 3),
      children: (
        <Text variant="createHelpText">
          {responses.length} {t('admin.assignments.publish.content.pieces')}
        </Text>
      ),
    },
    {
      title: t('admin.assignments.publish.settings.label'),
      onEditClick: () => setStepIndex(simpleType === 'pointInTime' ? 3 : 4),
      children: (
        <Stack>
          {useCustomDueDate && customDueDate && (
            <Text variant="createHelpText">
              {t('admin.assignments.publish.settings.due')}{' '}
              {format(new Date(customDueDate), 'MMM do, yyyy')}
            </Text>
          )}

          {!useCustomDueDate && daysToComplete && (
            <Text variant="createHelpText">
              {t('admin.assignments.publish.settings.dueIn')} {daysToComplete}{' '}
              {t('admin.assignments.publish.settings.days')}
            </Text>
          )}

          <HStack>
            {requireRecompletion ? <PreviewCheck /> : <PreviewX />}

            <Text variant="createHelpText">
              {t('admin.assignments.publish.settings.requireUnique')}
            </Text>
          </HStack>

          <HStack>
            {escalateManager ? <PreviewCheck /> : <PreviewX />}

            <Text variant="createHelpText">
              {t('admin.assignments.publish.settings.escalateManager')}
            </Text>
          </HStack>

          <HStack>
            {escalateBuddy ? <PreviewCheck /> : <PreviewX />}

            <Text variant="createHelpText">
              {t('admin.assignments.publish.settings.escalateBuddy')}
            </Text>
          </HStack>

          <HStack>
            {escalationContacts.length > 0 ? <PreviewCheck /> : <PreviewX />}

            <Text variant="createHelpText">
              {t('admin.assignments.publish.settings.escalationContactsAdded')}
            </Text>
          </HStack>
        </Stack>
      ),
    },
    {
      title: t('admin.assignments.publish.notifications.label'),
      onEditClick: () => (simpleType === 'pointInTime' ? setStepIndex(4) : setStepIndex(5)),
      children: (
        <Text variant="createHelpText">
          {[...reminderMessages, ...nonReminderMessages, ...overdueMessages].length}{' '}
          {[...reminderMessages, ...nonReminderMessages, ...overdueMessages].length === 0 ||
          [...reminderMessages, ...nonReminderMessages, ...overdueMessages].length > 1
            ? t('admin.assignments.publish.notifications.notificationsAdded')
            : t('admin.assignments.publish.notifications.notificationAdded')}
        </Text>
      ),
    },
  ];

  return (
    <AssignmentBuilderFormContainer
      headerComponent={
        <Flex justifyContent="space-between" alignItems="center">
          <Text fontSize="24px" fontWeight={600}>
            {t('admin.assignments.publish.header')}
          </Text>
        </Flex>
      }
      formComponent={
        <Box>
          {sectionOptions.map((option, index) => (
            <PublishSection
              key={option.title}
              title={option.title}
              useBottomBorder={index !== sectionOptions.length - 1}
              renderEditButton={option.renderEditButton}
              onEditClick={option.onEditClick}
            >
              {option.children}
            </PublishSection>
          ))}
        </Box>
      }
    />
  );
}
